import { observer } from 'mobx-react'
import React from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { captureException } from '@flowwai-frontend/shared/utils/web'

import {
   defaultTheme as designSystemDefaultTheme,
   ThemeContextProvider as DesignSystemThemeProvider
} from '@flowwai-frontend/design-system'
import { FallBackErrorBoundaryType } from '@flowwai-frontend/libs/shared/types'

import ErrorBoundaryView from '../ErrorBoundaryView/ErrorBoundaryView'

interface Props {
   children: React.ReactNode

   falBackErrorViewContainerStyles?: string
   shouldShowActionItem?: boolean
   shouldWrapWithThemeContext?: boolean

   onClickActionItem?: (resetErrorBoundary: () => void) => void
   renderCustomErrorView?: () => React.ReactNode

   context?: Record<string, any>
}

const ErrorBoundaryWrapper = (props: Props): React.ReactElement => {
   const {
      children,
      shouldWrapWithThemeContext = false,
      falBackErrorViewContainerStyles,
      renderCustomErrorView,
      context,
      ...rest
   } = props

   const renderFallBackErrorView = (props: FallbackProps): React.ReactNode => {
      captureException(props.error, context)

      return (
         <ErrorBoundaryView
            containerClassName={falBackErrorViewContainerStyles}
            errorConfig={props.error as FallBackErrorBoundaryType}
            {...rest}
         />
      )
   }

   const renderCustomFallBackErrorView = (
      props: FallbackProps
   ): React.ReactNode => {
      captureException(props.error, context)

      return renderCustomErrorView?.()
   }

   const renderErrorBoundary = (): React.ReactElement => (
      <ErrorBoundary
         fallbackRender={
            renderCustomErrorView
               ? renderCustomFallBackErrorView
               : renderFallBackErrorView
         }
      >
         {children}
      </ErrorBoundary>
   )

   const renderErrorBoundaryWithThemeContext = (): React.ReactElement => (
      <DesignSystemThemeProvider theme={designSystemDefaultTheme}>
         {renderErrorBoundary()}
      </DesignSystemThemeProvider>
   )

   return shouldWrapWithThemeContext
      ? renderErrorBoundaryWithThemeContext()
      : renderErrorBoundary()
}

export default observer(ErrorBoundaryWrapper)
